import useGTM from "@/hooks/useGTM";
import { useStores } from "@/hooks/useStores";
import { pushEvent } from "@/lib/gtm";
import FeatureList from "@/src/features/onboarding/components/FeatureList";
import { useCountryStripeProduct } from "@/src/features/pricing/hooks/useCountryStripeProduct";
import { Tab } from "@headlessui/react";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import { SimplifiedPrice, SubscriptionCard } from "./SubscriptionCard";

interface SubscriptionCardsProps {
  hasHadATrial: boolean;
  couponCode: string;
  variant?: string;
  currentStepIndex: number;
  logOnboardingStep: (
    currentStepIndex: number,
    currentStepKey: string,
    currentUser: User,
    otherObjective: string,
    otherText: string,
    otherCategory: string
  ) => void;
  otherObjective: string;
  otherText: string;
  otherCategory: string;
  displayCurrency?: string | undefined;
}

interface User {
  id?: string | number;
  email?: string;
}

const SubscriptionCards = ({
  hasHadATrial,
  couponCode,
  variant,
  currentStepIndex,
  logOnboardingStep,
  otherObjective,
  otherText,
  otherCategory,
}: SubscriptionCardsProps) => {
  const gtm = useGTM();
  const router = useRouter();

  const handleContinueAsFree = () => {
    gtm.pushEvent("onboarding_step_completed", {
      onboardingStepName: "onboardingChooseYourPlan",
      onboardingChooseYourPlan: ["selectedPlan:  free"],
    });

    router.push("/home");
  };

  return (
    <>
      <SubscriptionPlanTabs
        hasHadATrial={hasHadATrial}
        couponCode={couponCode}
        variant={variant}
        currentStepIndex={currentStepIndex}
        logOnboardingStep={logOnboardingStep}
        otherObjective={otherObjective}
        otherText={otherText}
        otherCategory={otherCategory}
      />

      <div className="flex h-full flex-col justify-between gap-10 pt-6">
        <FeatureList />
        <button
          type="button"
          onClick={handleContinueAsFree}
          className="mt-auto flex w-full items-center justify-center gap-2 rounded-lg px-2 py-4 outline outline-1 outline-offset-[-1px] outline-zinc-200"
        >
          <div className="justify-start text-center font-body text-sm font-medium text-zinc-500">
            Continue as a free member with limited access
          </div>
        </button>
      </div>
    </>
  );
};

interface SubscriptionPlanTabsProps {
  hasHadATrial: boolean;
  couponCode: string;
  variant?: string;
  currentStepIndex: number;
  logOnboardingStep: (
    currentStepIndex: number,
    currentStepKey: string,
    currentUser: User, // User type for currentUser
    otherObjective: string,
    otherText: string,
    otherCategory: string
  ) => void;
  otherObjective: string;
  otherText: string;
  otherCategory: string;
}

const SubscriptionPlanTabs = ({
  hasHadATrial,
  couponCode,
  variant,
  currentStepIndex,
  logOnboardingStep,
  otherObjective,
  otherText,
  otherCategory,
}: SubscriptionPlanTabsProps) => {
  const { prices, isLoading, error } = useCountryStripeProduct();
  const { authStore } = useStores();

  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    price: SimplifiedPrice, // Using SimplifiedPrice from SubscriptionCard.tsx
    planType: "yearly" | "monthly"
  ) => {
    e.preventDefault();
    const cost = price.unit_amount;

    const currentUser = authStore.currentUser as User | undefined; // Cast to User

    posthog.capture("onboarding_plan_selected", {
      userId: currentUser?.id,
      plan: planType,
      cost,
      variant,
    });

    localStorage.setItem("checkout_frequency", planType);
    localStorage.setItem("checkout_trial", !hasHadATrial ? "true" : "false");
    localStorage.setItem("checkout_advertised_cost", cost?.toString() ?? "0");

    pushEvent("checkout_start", {
      frequency: planType,
      advertised_cost: cost,
      trial: !hasHadATrial,
    });

    if (currentUser) {
      logOnboardingStep(
        currentStepIndex,
        "onboardingChooseYourPlan",
        currentUser,
        otherObjective,
        otherText,
        otherCategory
      );
    }

    e.currentTarget.submit();
  };

  if (!isLoading && !prices?.length) {
    return <p>No pricing plans available.</p>;
  }

  return (
    <Tab.Group>
      <Tab.List className="tab-list-sm w-max">
        <Tab className="tab">Annual</Tab>
        <Tab className="tab">Monthly</Tab>
      </Tab.List>
      <Tab.Panels>
        {(["yearly", "monthly"] as const).map((planType) => {
          const isYearly = planType === "yearly";
          const interval = isYearly ? "year" : "month";

          if (isLoading) {
            return (
              <Tab.Panel key={planType}>
                <form
                  className="mx-auto w-full"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <button type="button" className="w-full" disabled>
                    <SubscriptionCard
                      isYearly={isYearly}
                      hasHadATrial={hasHadATrial}
                      isLoading={true}
                    />
                  </button>
                </form>
              </Tab.Panel>
            );
          }

          // Ensure prices is not undefined before finding
          const price =
            prices && prices.find((p) => p.recurring?.interval === interval);

          if (!price) {
            return (
              <Tab.Panel key={planType} className="pt-4">
                <p>
                  {isYearly ? "Annual" : "Monthly"} plan not currently
                  available.
                </p>
              </Tab.Panel>
            );
          }

          return (
            <Tab.Panel key={price.id}>
              <form
                action="/api/plus/checkout"
                method="POST"
                data-rewardful="true"
                onSubmit={(e) => handleSubmit(e, price, planType)}
                className="mx-auto w-full"
              >
                {couponCode && (
                  <input type="hidden" name="coupon" value={couponCode} />
                )}
                <input type="hidden" name="priceId" value={price.id} />
                <input
                  type="hidden"
                  name="nGNndCdFpnDazku"
                  value={!hasHadATrial ? "scUmYobrToNvMkY" : "WrmUyxZMkBbxBrU"}
                />
                <input
                  type="hidden"
                  name="email"
                  value={
                    (authStore.currentUser as User | undefined)?.email ?? ""
                  }
                />
                <button type="submit" className="w-full">
                  <SubscriptionCard
                    price={price} // Pass the full price object
                    isYearly={isYearly}
                    hasHadATrial={hasHadATrial}
                    isLoading={false}
                  />
                </button>
              </form>
            </Tab.Panel>
          );
        })}
      </Tab.Panels>
    </Tab.Group>
  );
};

export default SubscriptionCards;
